import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Technical-SEO baseline (contract §5.S3, LEG-1). The QLearn app is the
// CONVERSION surface, not the SEO surface (that is medlearnity.com/qlearn/).
// Only the brand/navigational entry points should be indexable; everything
// else is authed or app-internal and must not land in Google's index.
//
// INDEXABLE_PATHS is an allow-list of EXACT pathnames. The bare root serves
// the login/landing to anonymous visitors (crawlers), so both `/` and
// `/login` stay indexable; every other route emits robots=noindex.
export const INDEXABLE_PATHS = ["/", "/login"];

// Pure: does this pathname get a `<meta name="robots" content="noindex">`?
// Returns the robots content string, or null when the route is indexable.
// Trailing slashes are normalized so `/exam/` and `/exam` behave the same.
export const getRobotsForPath = (pathname) => {
  // Strip trailing slashes so `/exam/` and `/exam` resolve identically, using a
  // simple loop instead of a backtracking regex. The bare root is left as `/`;
  // any other all-slashes path reduces to "" (noindex) — matching the prior
  // `replace(/\/+$/, "")` behavior exactly.
  let normalized = pathname;
  if (normalized !== "/") {
    while (normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }
  }
  return INDEXABLE_PATHS.includes(normalized) ? null : "noindex";
};

const ROBOTS_SELECTOR = 'meta[name="robots"][data-route-meta="1"]';

// Imperatively reconcile the robots meta tag with the current route. There is
// no react-helmet-class dependency in this repo, so we manage the single tag
// on document.head directly (idempotent: one tagged node, added/removed as the
// route changes). The `data-route-meta` marker keeps us from ever touching a
// robots tag authored elsewhere.
export const applyRobotsMeta = (pathname, doc) => {
  const head = doc.head || doc.getElementsByTagName("head")[0];
  if (!head) return;
  const robots = getRobotsForPath(pathname);
  let tag = head.querySelector(ROBOTS_SELECTOR);
  if (robots) {
    if (!tag) {
      tag = doc.createElement("meta");
      tag.setAttribute("name", "robots");
      tag.dataset.routeMeta = "1"; // -> data-route-meta="1"
      head.appendChild(tag);
    }
    tag.setAttribute("content", robots);
  } else if (tag) {
    tag.remove();
  }
};

// Mount once inside <Router>; re-applies on every navigation.
const RouteMetaManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    applyRobotsMeta(pathname, document);
  }, [pathname]);
  return null;
};

export default RouteMetaManager;
